import { Runtime } from "../41worker/Runtime";
import { Drive } from "../fs/";
import { MessageData } from "../types/messageEvent";

export interface WasmExports {
  memory: WebAssembly.Memory;
  [key: string]: any;
}

// Simple text encoding/decoding for AssemblyScript communication
class AsmTextHelper {
  // Decode AssemblyScript string from memory pointer
  static decodeText(memory: WebAssembly.Memory, ptr: number): string {
    if (!ptr) return "";
    
    try {
      // AssemblyScript string layout: [length (4 bytes)][data...]
      const view = new DataView(memory.buffer);
      const len = view.getUint32(ptr - 4, true); // little-endian
      
      const stringData = new Uint8Array(memory.buffer, ptr, len);
      return new TextDecoder().decode(stringData);
    } catch (e) {
      console.error(`[AsmTextHelper] decodeText error:`, e);
      return "";
    }
  }

  // Encode text as a sequence of integers for syscall parameters
  static encodeText(text: string): number[] {
    const encoder = new TextEncoder();
    const bytes = encoder.encode(text);
    const ints: number[] = [];

    // First int is the length
    ints.push(bytes.length);

    // Pack 4 bytes into each integer (32 bits)
    for (let i = 0; i < bytes.length; i += 4) {
      let value = 0;
      for (let j = 0; j < 4 && i + j < bytes.length; j++) {
        value |= bytes[i + j] << (j * 8);
      }
      ints.push(value);
    }

    return ints;
  }

  // Decode integers back to text (inverse of encodeText)
  static decodeInts(ints: number[]): string {
    if (ints.length === 0) return "";

    const length = ints[0];
    const bytes = new Uint8Array(length);

    for (let i = 1; i < ints.length; i++) {
      const value = ints[i];
      const offset = (i - 1) * 4;

      for (let j = 0; j < 4 && offset + j < length; j++) {
        bytes[offset + j] = (value >> (j * 8)) & 0xff;
      }
    }

    return new TextDecoder().decode(bytes);
  }
}

export default class AsmRuntime extends Runtime {
  wasmBytes: Uint8Array | undefined;
  protected wasmInstance: WebAssembly.Instance | undefined;
  protected exports: WasmExports | undefined;
  protected importObject: WebAssembly.Imports;
  pendingRequests: Map<
    number,
    { resolve: (value: any) => void; reject: (reason: any) => void }
  > = new Map();
  streamEventListeners: Map<string | number, ((data: any) => void)[]> =
    new Map();

  constructor(
    path: string,
    cmdLine: string,
    env: { [key: string]: string },
    flags?: { [key: string]: boolean }
  ) {
    super(path, cmdLine, env, flags);

    // Create a more basic import object with minimal dependencies
    this.importObject = {
      env: {
        memory: new WebAssembly.Memory({ initial: 256 }), // 16MB initial memory

        // Unified logging function that handles string pointers from AssemblyScript
        log: (ptr: number) => {
          if (!ptr) return;

          try {
            // use the imported memory from the import object since were using --importMemory
            const memory = this.importObject.env.memory as WebAssembly.Memory;
            
            if (memory) {
              const str = AsmTextHelper.decodeText(memory, ptr);
              console.log(`[WASM] ${str}`);
            } else {
              console.error(`[WASM log] No memory found in import object`);
            }
          } catch (e) {
            console.error(`[WASM log] Error: ${e}`);
          }
        },

        // Enhanced syscall that supports bi-directional communication
        syscall: (opCode: number, ...args: number[]) => {
          // Special opcodes for bi-directional communication
          switch (opCode) {
            case 1000: // WASM responding to a kernel request
              const callID = args[0];
              const responseData = args.slice(1);
              const decodedResponse = AsmTextHelper.decodeInts(responseData);
              this.respondToKernelRequest(callID, decodedResponse);
              return 1;
              
            case 1001: // WASM requesting data from kernel (async)
              const requestCallID = args[0];
              const requestOpCode = args[1];
              const requestData = args.slice(2);
              // Handle async request from WASM to kernel
              console.log(`[AsmRuntime] WASM async request: ${requestOpCode}`, requestData);
              return 1;
              
            default:
              // Regular syscalls
              return this.handleNumericSysCall(opCode, args);
          }
        },

        // Error handling
        abort: (msg: number, file: number, line: number, column: number) => {
          console.error(`[WASM] Abort: ${msg} at ${file}:${line}:${column}`);
          throw new Error(`WASM module aborted execution`);
        },
      },
    };
  }

  // Handle system calls that use numeric codes and parameters
  private async handleNumericSysCall(opCode: number, args: number[]): Promise<number> {
    // Parse opCode to determine operation
    switch (opCode) {
      case 1: // File operations
        return await this.handleFileOperation(args);
      case 2: // Console output
        const message = AsmTextHelper.decodeInts(args);
        console.log(`[WASM Console] ${message}`);
        return 1;
      case 3: // Return a string to WASM
        // For this operation, we return the encoded integers that represent a string
        // The caller will need to decode these integers
        const responseStr = "Response from runtime";
        const encodedResponse = AsmTextHelper.encodeText(responseStr);
        // We can't return the full array, but we can write it to a specific memory location
        // or return a simple success code here
        return 1;
      default:
        console.error(`[AssemblyRuntime] Unknown operation code: ${opCode}`);
        return 0; // Error code
    }
  }

  private async handleFileOperation(args: number[]): Promise<number> {
    const operation = args[0]; // First argument is the file operation type
    const pathData = args.slice(1); // Remaining args encode the file path
    const path = AsmTextHelper.decodeInts(pathData);

    switch (operation) {
      case 1: // Create file
        const message: MessageData = { op: "fs.createFile", args: [path] };
        const result = await this.handleReceivedRequest(message);
        return result ? 1 : 0;
      case 2: // Create directory
        const dirMessage: MessageData = { op: "fs.createDir", args: [path] };
        const dirResult = await this.handleReceivedRequest(dirMessage);
        return dirResult ? 1 : 0;
      default:
        return 0; // Error code
    }
  }

  async exec() {
    const file = await Drive.getByDriveLetter("C")?.driverInstance?.read(
      this.path.slice(3)
    );
    if (!file || file instanceof Error) throw new Error("File not found");

    this.state = "running";
    try {
      this.wasmBytes = new Uint8Array(await file.arrayBuffer());
      const { instance } = await WebAssembly.instantiate(
        this.wasmBytes,
        this.importObject
      );
      this.wasmInstance = instance;
      this.exports = instance.exports as WasmExports;

      // Call the start function if it exists
      if (typeof this.exports._INIT === "function") {
        const result = this.exports._INIT();
        console.log(
          `[AssemblyRuntime] WASM module started, returned: ${result}`
        );
      } else {
        console.error(`[AssemblyRuntime] WASM module has no _INIT function`);
      }
    } catch (e) {
      console.error(`[AssemblyRuntime] Error running WASM module: ${e}`);
      this.terminate();
    }
  }

  terminate(): void {
    if (this.state !== "running") throw new Error("WASM instance not running");
    this.state = "terminated";
    // Perform any necessary cleanup
  }

  postMessage(data: any): Promise<any> {
    // Delegate to the new sendMessage method for consistency
    return this.sendMessage(data);
  }

  // Helper method to encode complex data into numeric arrays for WASM
  private encodeDataForWasm(data: any): number[] {
    if (typeof data === "string") {
      return AsmTextHelper.encodeText(data);
    } else if (typeof data === "object") {
      // Convert object to JSON string, then encode
      return AsmTextHelper.encodeText(JSON.stringify(data));
    } else if (typeof data === "number") {
      return [data];
    } else {
      return [0]; // Default value for unsupported types
    }
  }

  postStream(data: any): void {
    if (this.state !== "running") throw new Error("Runtime not running");

    console.log(`[AssemblyRuntime] Streamed Event\n`, data);
    this.handleStreamEvent(data);
  }

  handleStreamEvent(data: any): void {
    if (data) {
      let eventName = data.op;
      if (!data.op) {
        eventName = data;
      }

      this.streamEventListeners.get(eventName)?.forEach((func) => {
        func(data);
      });

      switch (eventName) {
        case 20:
          this.terminate();
          break;
        case 2:
          console.log("received heartbeat");
          break;
      }
    } else {
      console.error(
        `[AssemblyRuntime] Received malformed Streamed Event: ${data}`
      );
    }
  }

  // ============ BI-DIRECTIONAL DUPLEX METHODS ============

  // Kernel → Executable: Call a specific exported function in the WASM module
  callWasmFunction(functionName: string, ...args: number[]): number | undefined {
    if (this.state !== "running" || !this.exports) {
      console.error(`[AsmRuntime] Cannot call ${functionName}: runtime not ready`);
      return undefined;
    }

    if (typeof this.exports[functionName] === "function") {
      try {
        return this.exports[functionName](...args);
      } catch (e) {
        console.error(`[AsmRuntime] Error calling ${functionName}:`, e);
        return undefined;
      }
    } else {
      console.error(`[AsmRuntime] Function ${functionName} not exported by WASM module`);
      return undefined;
    }
  }

  // Kernel → Executable: Send an event to the WASM module
  sendEvent(eventType: number, eventData: string): boolean {
    if (this.state !== "running") return false;

    // Encode the event data and send to WASM
    const encodedData = AsmTextHelper.encodeText(eventData);
    const result = this.callWasmFunction("handle_event", eventType, ...encodedData);
    return result === 1; // Success
  }

  // Kernel → Executable: Send a message and optionally wait for response
  sendMessage(data: any): Promise<any> {
    if (this.state !== "running") throw new Error("Runtime not running");

    const callID = Math.floor(Math.random() * 10000000);
    console.log(`[AsmRuntime] (${callID}) Kernel → WASM Request:`, data);

    return new Promise((resolve, reject) => {
      if (!data) {
        reject(new Error("No data provided"));
        return;
      }

      this.pendingRequests.set(callID, { resolve, reject });

      // Try to call WASM message handler first
      if (this.exports && typeof this.exports.handle_message === "function") {
        try {
          // Convert operation to opcode
          const opCode = this.getOpCodeForOperation(data.op);
          
          // Encode the data for WASM
          const encodedData = this.encodeDataForWasm(data);
          
          // Call WASM handler
          const response = this.exports.handle_message(callID, opCode, ...encodedData);
          
          // If WASM returns immediately (not -1), resolve now
          if (response !== -1) {
            const pendingRequest = this.pendingRequests.get(callID);
            if (pendingRequest) {
              this.pendingRequests.delete(callID);
              pendingRequest.resolve(response);
            }
          }
          // Otherwise, wait for WASM to call back via syscall with response opcode
          
        } catch (e) {
          console.error(`[AsmRuntime] Error calling WASM handler:`, e);
          // Fallback to runtime handling
          this.handleMessageInRuntime(callID, data);
        }
      } else {
        // Fallback to runtime handling if WASM doesn't have message handler
        this.handleMessageInRuntime(callID, data);
      }
    });
  }

  private getOpCodeForOperation(op: string): number {
    switch (op) {
      case "fs.createFile": return 1;
      case "fs.createDir": return 2;
      case "fs.readFile": return 3;
      case "fs.writeFile": return 4;
      case "fs.deleteFile": return 5;
      case "ipc.send": return 10;
      case "ipc.listen": return 11;
      case "ipc.broadcast": return 12;
      default: return 99; // Unknown
    }
  }

  private async handleMessageInRuntime(callID: number, data: any): Promise<void> {
    // Original runtime-based message handling for fallback
    const dataArgs = Array.isArray(data.data) ? data.data : [data.data];
    const response = await this.handleReceivedRequest({ op: data.op, args: dataArgs });
    
    const pendingRequest = this.pendingRequests.get(callID);
    if (pendingRequest) {
      this.pendingRequests.delete(callID);
      pendingRequest.resolve(response);
    }
  }

  // Allow WASM to respond to kernel requests asynchronously via syscall
  private respondToKernelRequest(callID: number, response: any): void {
    const pendingRequest = this.pendingRequests.get(callID);
    if (pendingRequest) {
      this.pendingRequests.delete(callID);
      pendingRequest.resolve(response);
      console.log(`[AsmRuntime] (${callID}) WASM → Kernel Response:`, response);
    } else {
      console.warn(`[AsmRuntime] No pending request found for callID: ${callID}`);
    }
  }

  // ============ END BI-DIRECTIONAL DUPLEX METHODS ============
}
